import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Smartphone,
  Mail,
  ScrollText,
  House,
  LogOut,
  FileText,
} from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import logo from '../../assets/logos/inglelewer_en.png';
import ContainerComponent from '../Container/ContainerComponent';
import MobileNavigation from './MobileNavigation';
import { INavigation } from '../../types/navigation';
import useFetchAuthMetadata from '../../hooks/useFetchAuthMetadata';

const navLinksData: INavigation[] = [
  {
    name: 'Dashboard',
    href: '/',
    image: (
      <House className="text-inglelewer-pink mt-1" width={16} height={16} />
    ),
    subNav: null,
    userMenu: false,
  },
  {
    name: 'Policy Navigator',
    href: '/policies',
    image: (
      <ScrollText
        className="text-inglelewer-pink mt-1"
        width={16}
        height={16}
      />
    ),
    subNav: null,
    userMenu: false,
  },
  {
    name: 'Documents',
    href: '/documents',
    image: (
      <FileText className="text-inglelewer-pink mt-1" width={16} height={16} />
    ),
    subNav: null,
    userMenu: false,
  },
  {
    name: 'Claims',
    href: '/claims',
    image: (
      <FileText className="text-inglelewer-pink mt-1" width={16} height={16} />
    ),
    subNav: null,
    userMenu: false,
  },
  {
    name: 'Sign out',
    href: '/',
    image: (
      <LogOut className="text-inglelewer-pink mt-1" width={16} height={16} />
    ),
    subNav: null,
    userMenu: true,
    logout: true,
  },
];

const customerSupportData: INavigation[] = [
  {
    name: '1-888-575-1231',
    href: 'tel:1-888-575-1231',
    image: (
      <Smartphone
        className="text-inglelewer-pink mt-1"
        width={16}
        height={16}
      />
    ),
    subNav: null,
    userMenu: false,
  },
  {
    name: 'customersupport@inglelewer.ca',
    href: 'mailto:customersupport@inglelewer.ca',
    image: (
      <Mail className="text-inglelewer-pink mt-1" width={16} height={16} />
    ),
    subNav: null,
    userMenu: false,
  },
];

const Navigation = () => {
  const [isOpen, toggleOpen] = useState(false);
  const [serviceIsOpen, toggleServiceOpen] = useState(false);
  const [mobileIsOpen, toggleMobileOpen] = useState(false);
  const pathname = useLocation();
  const { isLoading, user, loginWithRedirect, logout } = useAuth0();
  const { metadata } = useFetchAuthMetadata();
  const isGroupIdsValid =
    Array.isArray(metadata?.management?.groupIds) &&
    metadata?.management?.groupIds?.length > 0;

  return (
    <div className="z-50">
      <nav className="border border-solid border-gray-200">
        <ContainerComponent>
          <div className="flex py-2 md:py-4 justify-between items-center">
            <div className="hidden lg:flex">
              <Link to="/">
                <img src={logo} width={350} />
              </Link>
            </div>

            <div className="ml-auto flex-col hidden lg:flex pb-2">
              {!isLoading &&
                (user && isGroupIdsValid ? (
                  <>
                    <div className="h-8 flex items-center justify-end">
                      {customerSupportData.map(item => (
                        <div
                          className="hover:underline text-inglelewer-pink pl-6 flex justify-between items-center"
                          key={item.name}
                        >
                          {item.image}
                          <a href={item.href} className="pl-1">
                            {item.name}
                          </a>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-4 lg:space-x-8 items-center justify-end">
                      <>
                        {navLinksData
                          .filter(item => !item.userMenu)
                          .map(item =>
                            item.subNav === null ? (
                              <Link
                                key={item.name}
                                to={item.href}
                                className={`px-1 items-center inline-flex font-medium text-center text-sm lg:text-base ${
                                  pathname.href === item.href
                                    ? 'pt-0.5 border-b-2 border-b-ingle-light-purple text-ingle-light-purple'
                                    : 'text-gray-700'
                                }`}
                                onClick={() => toggleServiceOpen(false)}
                              >
                                {item.name}
                              </Link>
                            ) : (
                              <div
                                key={item.name}
                                className={`relative px-1 items-center inline-flex font-medium text-sm lg:text-base ${
                                  pathname.href === '/services/'
                                    ? 'pt-0.5 border-b-2 border-b-ingle-light-purple text-ingle-light-purple'
                                    : 'text-gray-700'
                                }`}
                              >
                                <button
                                  type="button"
                                  id="my-services-button"
                                  aria-expanded="false"
                                  aria-haspopup="true"
                                  onClick={() =>
                                    toggleServiceOpen(!serviceIsOpen)
                                  }
                                >
                                  <span className="absolute -inset-1.5"></span>
                                  <span className="sr-only">
                                    Open my services menu
                                  </span>
                                  <p className="font-medium text-gray-700">
                                    {item.name}
                                  </p>
                                </button>

                                {serviceIsOpen ? (
                                  <div
                                    className="absolute z-30 mt-72 w-52 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="my-services-button"
                                    tabIndex={-1}
                                  >
                                    {item.subNav.map(subNavElements => (
                                      <Link
                                        key={subNavElements.name}
                                        to={subNavElements.href}
                                        className="block px-4 py-2 text-sm text-gray-700"
                                        role="menuitem"
                                        tabIndex={-1}
                                        onClick={() => toggleServiceOpen(false)}
                                      >
                                        {subNavElements.name}
                                      </Link>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            )
                          )}
                      </>
                    </div>
                  </>
                ) : null)}
            </div>

            <div className="hidden lg:flex">
              <div className="ml-4 flex items-center md:ml-6">
                {/* Profile dropdown */}
                {!isLoading &&
                  (user ? (
                    <div className="relative ml-3">
                      <div>
                        <button
                          type="button"
                          className="relative flex max-w-xs items-center rounded-full bg-ingle-light-purple text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-ingle-light-purple hover:cursor-pointer"
                          id="user-menu-button"
                          aria-expanded="false"
                          aria-haspopup="true"
                          onClick={() => toggleOpen(!isOpen)}
                        >
                          <span className="absolute -inset-1.5"></span>
                          <span className="sr-only">Open user menu</span>

                          <img
                            className="h-8 w-8 rounded-full"
                            src={user.picture || ''}
                            width={200}
                            height={200}
                            alt="Profile Pic"
                            data-testid="profile-picture"
                          />
                        </button>
                      </div>

                      {/* Dropdown menu, show/hide based on menu state */}
                      {isOpen ? (
                        <div
                          className="flex flex-col absolute right-0 z-30 mt-4 min-w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="user-menu-button"
                          tabIndex={-1}
                          key={user.email}
                        >
                          {
                            <div
                              className="block px-4 py-2 text-sm text-gray-700"
                              role="menuitem"
                              tabIndex={-1}
                            >
                              {user.email}
                            </div>
                          }
                          {navLinksData
                            .filter(item => item.userMenu)
                            .map(menuItem =>
                              menuItem.logout ? (
                                <button
                                  className="w-full text-start block px-4 py-2 text-sm text-gray-700 hover:cursor-pointer"
                                  key={user.email}
                                  onClick={() =>
                                    logout({
                                      logoutParams: {
                                        returnTo: window.location.origin,
                                      },
                                    })
                                  }
                                >
                                  {menuItem.name}
                                </button>
                              ) : (
                                //  If it's an Auth0 link, it should be an <a> tag rather than <Link> --> https://community.auth0.com/t/nextjs-cors-no-access-control-allow-origin-header-is-present-on-the-requested-resource/125578/4
                                <a
                                  href={menuItem.href}
                                  className="block px-4 py-2 text-sm text-gray-700"
                                  role="menuitem"
                                  tabIndex={-1}
                                  key={menuItem.name}
                                >
                                  {menuItem.name}
                                </a>
                              )
                            )}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => loginWithRedirect()}
                        className="text-ingle-light-purple bg-white border-ingle-light-purple border-2 hover:bg-ingle-dark-purple hover:text-white rounded-md px-12 py-2 mx-5 text-sm font-bold hover:cursor-pointer"
                      >
                        Login
                      </button>

                      <button
                        onClick={() =>
                          loginWithRedirect({
                            authorizationParams: { screen_hint: 'signup' },
                          })
                        }
                        className="text-white bg-ingle-light-purple hover:bg-ingle-dark-purple hover:text-white rounded-md px-3 py-2.5 text-sm font-bold hover:cursor-pointer"
                      >
                        Create an account
                      </button>
                    </>
                  ))}
              </div>
            </div>
            <div className="-mr-2 flex lg:hidden">
              {/* Mobile menu button */}
              <button
                type="button"
                className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded="false"
                onClick={() => toggleMobileOpen(!mobileIsOpen)}
              >
                <span className="absolute -inset-0.5"></span>
                <span className="sr-only">Open main menu</span>

                {/* Icon when menu is closed. Menu open: "hidden", Menu closed: "block" */}

                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>

                {/* Icon when menu is open. Menu open: "block", Menu closed: "hidden" */}

                <svg
                  className="hidden h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div className="items-center inline-flex">
                <img
                  src={logo}
                  alt=""
                  className="hover:opacity-90 w-2/3 ml-2"
                />
              </div>
            </div>
          </div>
        </ContainerComponent>

        {mobileIsOpen ? (
          <div className="lg:hidden" id="mobile-menu">
            <div className="border-t border-gray-700 pb-3 pt-4">
              {!isLoading &&
                (user ? (
                  <>
                    <div className="flex items-center px-7">
                      <div className="flex-shrink-0">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={user.picture || ''}
                          width={256}
                          height={256}
                          alt="Profile Pic"
                          data-testid="profile-picture"
                        />
                      </div>
                      <div className="flex flex-row items-center space-x-2 ml-2">
                        <div className="text-sm font-xs font-bold leading-none text-black">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 px-2">
                      <MobileNavigation
                        navLinksData={navLinksData}
                        customerSupportData={customerSupportData}
                        isGroupIdsValid={isGroupIdsValid}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => loginWithRedirect()}
                      className="block w-full text-start rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white hover:cursor-pointer"
                    >
                      Login
                    </button>

                    <button
                      onClick={() =>
                        loginWithRedirect({
                          authorizationParams: { screen_hint: 'signup' },
                        })
                      }
                      className="block w-full text-start rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white hover:cursor-pointer"
                    >
                      Create an account
                    </button>
                  </>
                ))}
            </div>
          </div>
        ) : null}
      </nav>
    </div>
  );
};

export default Navigation;
